/**
 * The dashboards' shared filters (spec §4.1; D2 plan R19). Period, objective
 * and currency are shared by /overview and /channels through the URL and this
 * browser's memory. D2's own keys (type, campaign, active, tab), D3's
 * (sort, format, all; /creatives, D3 spec §4.6) and D4's (active, all;
 * /audience, D4 spec §4.7) live in the URL only and are never remembered.
 * useDashboardFilters parses D2's keys on every page, but /audience never
 * reads or writes them (its own keys go through useAudienceUrl), and links
 * between pages carry the shared keys only, so D2's `active` and D4's never
 * meet (D4 plan R19). Every value read is validated, and an
 * invalid one falls back silently, key by key: the URL, then the memory, then
 * the default. A currency is checked for its shape only. Whether the scope
 * holds it is currencyChoiceOf's call (D1 R8), which falls back to the most
 * spent.
 *
 * One more key rides D1's box 11 / 13 links: scope=all, the master's
 * all-workspaces view (I2). It is an arrival instruction, not a filter:
 * neither parser reads it, the memory never holds it, and the Channel
 * Deep-dive takes it out of the URL once it has applied it.
 *
 * Pure, except the two memory functions, which never throw: a browser that
 * refuses storage costs the reader the memory, never the page.
 *
 * @module services/dashboardFilters
 */
import { BUY_TYPES } from '@/services/looker/campaignNaming';
import { DATE_PRESET_IDS, isValidCustomRange } from '@/services/looker/lookerDateRange';
import { OBJECTIVE_FILTERS, type ObjectiveFilter } from '@/services/overview/overviewTypes';
import { BREAKDOWN_TABS, UNCLASSIFIED_TYPE, type BreakdownTab } from '@/services/channels/channelTypes';
import { CREATIVE_SORTS, FORMAT_FILTERS, type CreativeSort, type FormatFilter } from '@/services/creatives/creativeTypes';

export interface SharedFilters {
  /** A D1 period token: a preset id, or custom:YYYY-MM-DD:YYYY-MM-DD. */
  period: string;
  objective: ObjectiveFilter;
  /** The viewer's pick; null = the default (the most spent in the scope). */
  currency: string | null;
}

export interface ChannelUrlFilters {
  /** BUY_TYPES entries and/or UNCLASSIFIED_TYPE; [] = every type. */
  types: string[];
  /** Campaign uuids, lower case; [] = every campaign. */
  campaigns: string[];
  /** A: `1` / `0` in the URL; null = absent, so R11 decides. */
  active: boolean | null;
  tab: BreakdownTab;
}

/** D3's own keys (/creatives): the sort, the format filter and "แสดงทั้งหมด". */
export interface CreativeUrlFilters {
  sort: CreativeSort;
  format: FormatFilter;
  showAll: boolean;
}

/** D4's own keys (/audience): "เฉพาะ campaign ที่ Active" and "แสดงทั้งหมด". */
export interface AudienceUrlFilters {
  activeOnly: boolean;
  showAll: boolean;
}

export const DEFAULT_SHARED: SharedFilters = { period: '28d', objective: 'all', currency: null };
export const DEFAULT_CREATIVE_URL: CreativeUrlFilters = { sort: 'spend', format: 'all', showAll: false };
export const DEFAULT_AUDIENCE_URL: AudienceUrlFilters = { activeOnly: true, showAll: false };
export const FILTER_MEMORY_KEY = 'ydm.dashboardFilters.v1';

const SHARED_KEYS = ['period', 'objective', 'currency'] as const;
const CHANNEL_KEYS = ['type', 'campaign', 'active', 'tab'] as const;
const CREATIVE_KEYS = ['sort', 'format', 'all'] as const;
const AUDIENCE_KEYS = ['active', 'all'] as const;
const SCOPE_KEY = 'scope';
const SCOPE_ALL = 'all';
const CURRENCY = /^[A-Z]{3}$/;
const CUSTOM = /^custom:(\d{4}-\d{2}-\d{2}):(\d{4}-\d{2}-\d{2})$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const TYPE_VALUES: ReadonlySet<string> = new Set([...BUY_TYPES, UNCLASSIFIED_TYPE]);

// ---------------------------------------------------------------- validation

/** A D1 preset, or a custom range the server's rules accept (real dates, in order, ending before today, ≤ 3 × 366 days). */
export const isValidPeriodToken = (token: string, today: string): boolean => {
  if ((DATE_PRESET_IDS as readonly string[]).includes(token)) return true;
  const m = CUSTOM.exec(token);
  return m !== null && isValidCustomRange(m[1], m[2], today);
};

const isObjective = (v: unknown): v is ObjectiveFilter =>
  typeof v === 'string' && (OBJECTIVE_FILTERS as readonly string[]).includes(v);

const isCurrency = (v: unknown): v is string => typeof v === 'string' && CURRENCY.test(v);

/** The first candidate `valid` accepts (the URL's, then the memory's), else the fallback. */
const firstValid = <T, F>(candidates: readonly unknown[], valid: (v: unknown) => v is T, fallback: F): T | F =>
  candidates.find(valid) ?? fallback;

// ---------------------------------------------------------------- read

export const parseShared = (params: URLSearchParams, memory: Partial<SharedFilters> | null, today: string): SharedFilters => {
  const isPeriod = (v: unknown): v is string => typeof v === 'string' && isValidPeriodToken(v, today);
  return {
    period: firstValid([params.get('period'), memory?.period], isPeriod, DEFAULT_SHARED.period),
    objective: firstValid([params.get('objective'), memory?.objective], isObjective, DEFAULT_SHARED.objective),
    currency: firstValid([params.get('currency'), memory?.currency], isCurrency, DEFAULT_SHARED.currency),
  };
};

/** A comma-separated URL value: trimmed, blanks dropped. */
const listOf = (value: string | null): string[] =>
  (value ?? '').split(',').map((s) => s.trim()).filter((s) => s !== '');

/** Each value once, the first occurrence kept. */
const unique = (values: readonly string[]): string[] => values.filter((v, i) => values.indexOf(v) === i);

export const parseChannelUrl = (params: URLSearchParams): ChannelUrlFilters => {
  const active = params.get('active');
  const tab = params.get('tab');
  return {
    types: unique(listOf(params.get('type')).filter((v) => TYPE_VALUES.has(v))),
    // Postgres prints uuids in lower case; anything that is not a uuid cannot be a campaign id.
    campaigns: unique(listOf(params.get('campaign')).filter((v) => UUID.test(v)).map((v) => v.toLowerCase())),
    active: active === '1' ? true : active === '0' ? false : null,
    tab: BREAKDOWN_TABS.find((x) => x === tab) ?? 'campaign',
  };
};

/** An unknown value is the default (spec §4.6); all=1 is the only "show all". */
export const parseCreativeUrl = (params: URLSearchParams): CreativeUrlFilters => {
  const sort = params.get('sort');
  const format = params.get('format');
  return {
    sort: CREATIVE_SORTS.find((s) => s === sort) ?? DEFAULT_CREATIVE_URL.sort,
    format: FORMAT_FILTERS.find((f) => f === format) ?? DEFAULT_CREATIVE_URL.format,
    showAll: params.get('all') === '1',
  };
};

/** D4 spec §4.7: active=0 is the only "off" and all=1 the only "show all"; any other value is the default. */
export const parseAudienceUrl = (params: URLSearchParams): AudienceUrlFilters => ({
  activeOnly: params.get('active') !== '0',
  showAll: params.get('all') === '1',
});

// ---------------------------------------------------------------- write

/** A NEW params object: `params` without `keys`, then each pair that has a value (null or '' deletes). `params` is never touched. */
const rewrite = (
  params: URLSearchParams, keys: readonly string[], pairs: ReadonlyArray<readonly [string, string | null]>,
): URLSearchParams => new URLSearchParams([
  ...[...params].filter(([k]) => !keys.includes(k)),
  ...pairs.flatMap(([k, v]): Array<[string, string]> => (v ? [[k, v]] : [])),
]);

/** Period and objective always; currency only when one is picked. The other keys stay first, in their order. */
export const writeShared = (params: URLSearchParams, f: SharedFilters): URLSearchParams =>
  rewrite(params, SHARED_KEYS, [['period', f.period], ['objective', f.objective], ['currency', f.currency]]);

/** Each key only when it differs from its default: no type, no campaign, A undecided, the Campaign tab. */
export const writeChannelUrl = (params: URLSearchParams, c: ChannelUrlFilters): URLSearchParams =>
  rewrite(params, CHANNEL_KEYS, [
    ['type', c.types.join(',')],
    ['campaign', c.campaigns.join(',')],
    ['active', c.active === null ? null : c.active ? '1' : '0'],
    ['tab', c.tab === 'campaign' ? null : c.tab],
  ]);

/** Each key only when it differs from its default: Spend, every format, the first 15. */
export const writeCreativeUrl = (params: URLSearchParams, c: CreativeUrlFilters): URLSearchParams =>
  rewrite(params, CREATIVE_KEYS, [
    ['sort', c.sort === DEFAULT_CREATIVE_URL.sort ? null : c.sort],
    ['format', c.format === DEFAULT_CREATIVE_URL.format ? null : c.format],
    ['all', c.showAll ? '1' : null],
  ]);

/** Each key only when it differs from its default: Active on, the first 15. */
export const writeAudienceUrl = (params: URLSearchParams, a: AudienceUrlFilters): URLSearchParams =>
  rewrite(params, AUDIENCE_KEYS, [
    ['active', a.activeOnly ? null : '0'],
    ['all', a.showAll ? '1' : null],
  ]);

const sameValues = (a: readonly string[], b: readonly string[]): boolean => a.length === b.length && a.every((v, i) => v === b[i]);

/**
 * The URL holds D3's keys exactly as writeCreativeUrl would: each key once with its value, or absent for a default.
 * Compared by value with getAll, so a repeated key or a spelled-out default counts as not carried (R4).
 */
export const carriesCreativeUrl = (params: URLSearchParams, c: CreativeUrlFilters): boolean => {
  const canonical = writeCreativeUrl(new URLSearchParams(), c);
  return CREATIVE_KEYS.every((k) => sameValues(params.getAll(k), canonical.getAll(k)));
};

/** The URL holds D4's keys exactly as writeAudienceUrl would, compared by value with getAll (D4 plan R18). */
export const carriesAudienceUrl = (params: URLSearchParams, a: AudienceUrlFilters): boolean => {
  const canonical = writeAudienceUrl(new URLSearchParams(), a);
  return AUDIENCE_KEYS.every((k) => sameValues(params.getAll(k), canonical.getAll(k)));
};

/** What a link to the other dashboard carries: period=…&objective=…[&currency=…]. */
export const sharedSearchOf = (f: SharedFilters): string => writeShared(new URLSearchParams(), f).toString();

// ---------------------------------------------------------------- scope=all (I2)

/** D1's link to D2: the shared keys, then scope=all when D1 shows all workspaces. */
export const linkSearchOf = (f: SharedFilters, allWorkspaces: boolean): string =>
  rewrite(writeShared(new URLSearchParams(), f), [SCOPE_KEY], [[SCOPE_KEY, allWorkspaces ? SCOPE_ALL : null]]).toString();

/** The URL asks for the master's all-workspaces view. */
export const scopeAllAsked = (params: URLSearchParams): boolean => params.get(SCOPE_KEY) === SCOPE_ALL;

/** A NEW params object without the scope key; every other key kept, in its order. */
export const withoutScope = (params: URLSearchParams): URLSearchParams => rewrite(params, [SCOPE_KEY], []);

// ---------------------------------------------------------------- memory (localStorage; every access in try/catch)

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/** The stored object's well-typed fields only; parseShared still validates each value. */
const memoryOf = (o: Record<string, unknown>): Partial<SharedFilters> => ({
  ...(typeof o.period === 'string' ? { period: o.period } : {}),
  ...(isObjective(o.objective) ? { objective: o.objective } : {}),
  ...(typeof o.currency === 'string' || o.currency === null ? { currency: o.currency } : {}),
});

export const readFilterMemory = (): Partial<SharedFilters> | null => {
  try {
    const raw = localStorage.getItem(FILTER_MEMORY_KEY);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    return isRecord(parsed) ? memoryOf(parsed) : null;
  } catch {
    // Storage refused, or a value that is not JSON: no memory, never a broken page.
    return null;
  }
};

export const writeFilterMemory = (f: SharedFilters): void => {
  try {
    localStorage.setItem(FILTER_MEMORY_KEY, JSON.stringify({ period: f.period, objective: f.objective, currency: f.currency }));
  } catch {
    // A browser refusing storage costs the reader the memory, not the filters.
  }
};
